import { psAxios, type RequestConfig } from '@patsnap/common-ps-http';
import type { RouteLocationNormalizedGeneric } from 'vue-router';
import type { SharePageInterceptorService } from '../share-page-interceptor-service';
import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { RouterService } from '@telecom/shared/services/router';
import { isUserLogin } from '@telecom/shared/startup/user-auth';
import { ReportAccessStateService } from '@/services/report-access-state/report-access-state-service';
import { coreTelecomApiUrlPrefix } from '@telecom/shared/domain-config';

const UNIFIED_ACCESS_DENIED_ROUTES = new Set([
    'novelty-check-report',
    'fto-pro',
]);
const FTO_REPORT_DETAIL_PATH = /^\/?fto\/report\/[^/?]+\/?(?:\?.*)?$/;

/**
 * 分享页面拦截器服务
 * 该服务用于在分享页面中拦截请求，并为特定下游服务替换 baseURL。
 * Core Telecom 请求保持认证 API 地址，以便后端获取当前登录用户并校验定向邀请。
 * 该服务在路由变化时注册和注销拦截器
 */
@injectable()
export class SharePageInterceptorServiceImpl implements SharePageInterceptorService {
    private _requestInterceptorId: number | undefined = undefined;
    private _responseInterceptorId: number | undefined = undefined;
    private _ftoReportShareTaskId = '';
    private blackRouterList: { method: 'GET' | 'POST', url: string }[] = [
        // 黑名单路由,默认填写了一个为了写单元测试
        { method: 'GET', url: '/blacklist' },
    ];
    constructor(
        @inject(RouterService)
        private readonly _routerService: RouterService,
        @inject(ReportAccessStateService)
        private readonly _reportAccessStateService: ReportAccessStateService,
    ) { }

    public async registerAxiosInterceptor(): Promise<void> {
        this._routerService.beforeEach(async (to, from, next) => {
            if (this._isDifferentReport(to, from)) {
                this._reportAccessStateService.restore();
            }
            const isLogin = await isUserLogin();
            this._ftoReportShareTaskId = isLogin ? this._getFtoReportShareTaskId(to) : '';
            // 登录后的分享页才使用分享读取接口，普通页面始终保持原 API 地址。
            if (isLogin && this._isMatchSharePage(to)) {
                this._registerRequestInterceptor();
            } else {
                this._ejectRequestInterceptor();
            }
            if (isLogin && this._isMatchSharePage(to) && this._supportsUnifiedAccessDenied(to)) {
                this._registerResponseInterceptor();
            } else {
                this._ejectResponseInterceptor();
            }
            next();
        });
    }

    private _registerRequestInterceptor(): void {
        if (this._requestInterceptorId !== undefined) {
            return;
        }

        this._requestInterceptorId = psAxios.interceptors.request.use(this.interceptor.bind(this), undefined);
    }

    private _ejectRequestInterceptor(): void {
        if (this._requestInterceptorId === undefined) {
            return;
        }

        psAxios.interceptors.request.eject(this._requestInterceptorId);
        this._requestInterceptorId = undefined;
    }

    private _registerResponseInterceptor(): void {
        if (this._responseInterceptorId !== undefined) {
            return;
        }
        this._responseInterceptorId = psAxios.interceptors.response.use(
            undefined,
            this._handleResponseError.bind(this),
        );
    }

    private _ejectResponseInterceptor(): void {
        if (this._responseInterceptorId === undefined) {
            return;
        }
        psAxios.interceptors.response.eject(this._responseInterceptorId);
        this._responseInterceptorId = undefined;
    }

    private _handleResponseError(error: unknown): Promise<never> {
        this._reportAccessStateService.handleError(error);
        return Promise.reject(error);
    }

    private interceptorHandel(config: RequestConfig): RequestConfig {
        // 报告详情以 report_id 定位资源，额外传递分享任务标识供后端校验资源匹配与查看权限。
        if (this._ftoReportShareTaskId
            && (config.method ?? 'get').toLowerCase() === 'get'
            && FTO_REPORT_DETAIL_PATH.test(config.url ?? '')
            && config.baseURL?.replace(/\/$/, '') === coreTelecomApiUrlPrefix.replace(/\/$/, '')) {
            config.params = { ...config.params, task_id: this._ftoReportShareTaskId };
        }
        // 外观设计服务是 /fto-image-api/ 地址
        config.baseURL = config.baseURL?.replace('/fto-image-api/', '/public-share-image/');
        // 新版本查新
        config.baseURL = config.baseURL?.replace('/skill-chat', '/public-share/skill-chat');
        return config;
    }

    private _getFtoReportShareTaskId(route: RouteLocationNormalizedGeneric): string {
        if (route.name !== 'fto-pro') {
            return '';
        }
        const query = route.query ?? {};
        const taskId = query.taskId ?? query.task_id;
        if (typeof taskId === 'string' && taskId.startsWith('sh_')) {
            return taskId;
        }
        const shareId = query.shareId;
        return typeof shareId === 'string' && shareId ? `sh_${shareId}` : '';
    }

    private interceptor(config: RequestConfig): RequestConfig {
        if (this._isMatchBlackRouter(config)) {
            return config;
        }
        return this.interceptorHandel(config);
    }


    /**
     * 判断是否是分享页面
     * @param to 路由对象
     * @returns 是否是分享页面
     */
    private _isMatchSharePage(to: RouteLocationNormalizedGeneric): boolean {
        const query = to.query ?? {};
        const taskId = query.taskId ?? query.task_id;
        return !!query.shareId || (typeof taskId === 'string' && taskId.startsWith('sh_'));
    }

    private _supportsUnifiedAccessDenied(to: RouteLocationNormalizedGeneric): boolean {
        return UNIFIED_ACCESS_DENIED_ROUTES.has(String(to.name ?? ''));
    }

    private _isDifferentReport(
        to: RouteLocationNormalizedGeneric,
        from: RouteLocationNormalizedGeneric,
    ): boolean {
        const getReportKey = (route: RouteLocationNormalizedGeneric) => {
            const query = route.query ?? {};
            return [
                route.name,
                query.shareId,
                query.taskId,
                query.task_id,
            ].join(':');
        };
        return getReportKey(to) !== getReportKey(from);
    }

    /**
     * 判断是否是黑名单路由
     * @param config 请求配置
     * @returns 是否是黑名单路由
     */
    private _isMatchBlackRouter(config: RequestConfig): boolean {
        return !!this.blackRouterList?.find(path => {
            const methodMatch = config.method?.toLocaleLowerCase() === path.method.toLocaleLowerCase();
            const urlMatch = config.url && this._isExactUrlMatch(config.url, path.url);
            return methodMatch && urlMatch;
        });
    }

    /**
    * 精确匹配URL路径，确保不会匹配到子路由
    * @param requestUrl 请求URL
    * @param blacklistUrl 黑名单URL
    * @returns 是否匹配
    */
    private _isExactUrlMatch(requestUrl: string, blacklistUrl: string): boolean {
        // 移除开头和结尾的斜杠以标准化比较
        const normalizedRequestUrl = requestUrl.replace(/^\/|\/$/g, '');
        const normalizedBlacklistUrl = blacklistUrl.replace(/^\/|\/$/g, '');

        // 情况1: 完全相等
        if (normalizedRequestUrl === normalizedBlacklistUrl) {
            return true;
        }

        // 检查路径边界匹配
        const isPathBoundary = (index: number, str: string): boolean =>
            index < 0 || index >= str.length || str[index] === '/';

        // 以黑名单URL结尾，且边界正确
        if (normalizedRequestUrl.endsWith(normalizedBlacklistUrl)) {
            const boundaryIndex = normalizedRequestUrl.length - normalizedBlacklistUrl.length - 1;
            if (isPathBoundary(boundaryIndex, normalizedRequestUrl)) {
                return true;
            }
        }

        // 以黑名单URL开头，且边界正确
        if (normalizedRequestUrl.startsWith(normalizedBlacklistUrl)) {
            const boundaryIndex = normalizedBlacklistUrl.length;
            if (isPathBoundary(boundaryIndex, normalizedRequestUrl)) {
                return true;
            }
        }

        return false;
    }

}
